import { useCallback } from 'react';
import { useLocalStorageState } from '@reflex/ui/hooks/useWebStorageState';

export interface NotificationPrefs {
  enabled: boolean;
  onNeedsInput: boolean;
  onError: boolean;
  onCompleted: boolean;
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  enabled: false,
  onNeedsInput: true,
  onError: true,
  onCompleted: false,
};

const STORAGE_KEY = 'notifications-prefs';

export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export function getNotificationPermission(): NotificationPermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as NotificationPermissionState;
}

/**
 * Persisted per-user notification preferences + a setter.
 * Toggling `enabled` to true does NOT request permission — the caller (usually
 * the settings UI) should call `Notification.requestPermission()` and then set
 * `enabled` based on the result, so the prompt is tied to a user gesture.
 */
export function useNotificationPrefs(): {
  prefs: NotificationPrefs;
  setPrefs: (next: NotificationPrefs | ((prev: NotificationPrefs) => NotificationPrefs)) => void;
  update: <K extends keyof NotificationPrefs>(key: K, value: NotificationPrefs[K]) => void;
} {
  const [prefs, setPrefs] = useLocalStorageState<NotificationPrefs>(
    STORAGE_KEY,
    DEFAULT_NOTIFICATION_PREFS,
  );

  const update = useCallback(
    <K extends keyof NotificationPrefs>(key: K, value: NotificationPrefs[K]) => {
      setPrefs((prev) => ({ ...prev, [key]: value }));
    },
    [setPrefs],
  );

  return { prefs, setPrefs, update };
}
